/**
 * Generic 2D geometry over map points (§16). Nothing here knows about
 * locations, buildings, or the reducer — it is pure math the reducer's
 * `move()` (and world generation, which bakes footprints once into
 * `WorldState`) can lean on without either of them recomputing it.
 *
 * Content's building/filler shapes are SVG path strings. Every shape in
 * current content is a closed rectangle written as `M x y L x y L x y L x y
 * Z` (confirmed by inspection of `content/block/map.json`), but this parses
 * any sequence of `M`/`L` commands into a vertex list rather than assuming
 * four corners — and refuses (throws) on any other command instead of
 * silently mis-parsing it. Curve commands (C/S/Q/A) are not supported: there
 * are none in current content, so this is a fail-fast flag for later, not a
 * feature.
 */

import type { MapPoint } from './types/index.js';

/** Axis-aligned bounding box. */
export interface BBox {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** A polygon as parsed from a path: its vertices and their bounding box. */
export interface Footprint {
  readonly vertices: readonly MapPoint[];
  readonly bbox: BBox;
}

// Any letter is captured (not just M/L/Z) so an unsupported command — a
// curve (C/S/Q/A/T), or lowercase for relative coordinates, neither of
// which current content uses — is a token this function sees and can
// reject, rather than one the regex silently drops.
const PATH_TOKEN = /([A-Za-z])|(-?\d+(?:\.\d+)?)/g;

/**
 * Parse the point pairs following `M`/`L` in an SVG path string. `Z` closes
 * the polygon and carries no coordinates, so it is skipped. Any other
 * command letter throws rather than being ignored or guessed at (§19: fail
 * fast on content that doesn't match what the sim can read) — this includes
 * lowercase `m`/`l`/`z`, which in SVG mean relative-to-the-last-point and
 * would be silently wrong if read as absolute; there are none in current
 * content, so this is a flag for later, not a feature to build now.
 */
export function parsePathPoints(path: string): MapPoint[] {
  const tokens = path.match(PATH_TOKEN);
  if (!tokens) throw new Error(`Path has no parseable commands: ${JSON.stringify(path)}`);

  const points: MapPoint[] = [];
  let i = 0;
  while (i < tokens.length) {
    const command = tokens[i]!;
    if (command === 'Z') {
      i += 1;
      continue;
    }
    if (command !== 'M' && command !== 'L') {
      throw new Error(`Unsupported path command "${command}" in ${JSON.stringify(path)}: only M/L/Z are handled`);
    }
    const x = Number(tokens[i + 1]);
    const y = Number(tokens[i + 2]);
    if (tokens[i + 1] === undefined || tokens[i + 2] === undefined || !Number.isFinite(x) || !Number.isFinite(y)) {
      throw new Error(`${command} command missing its x y pair in ${JSON.stringify(path)}`);
    }
    points.push({ x, y });
    i += 3;
  }
  return points;
}

/** The smallest axis-aligned rectangle containing every vertex. */
export function boundingBox(vertices: readonly MapPoint[]): BBox {
  if (vertices.length === 0) throw new Error('boundingBox of an empty vertex list');
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const v of vertices) {
    if (v.x < minX) minX = v.x;
    if (v.x > maxX) maxX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.y > maxY) maxY = v.y;
  }
  return { minX, maxX, minY, maxY };
}

/** Parse a path string straight into a `Footprint` (vertices + their bbox). */
export function footprintOf(path: string): Footprint {
  const vertices = parsePathPoints(path);
  return { vertices, bbox: boundingBox(vertices) };
}

/**
 * Ray-casting point-in-polygon test. Standard even-odd rule: a point exactly
 * on an edge can resolve either way depending on which edge it's nearest to
 * in the winding order — that's an accepted property of the algorithm, not a
 * bug, and this sim never needs an authoritative answer for a point exactly
 * on a boundary (`move()` only calls this to decide whether to push a point
 * *out*, and a point already outside needs no push).
 */
export function pointInPolygon(point: MapPoint, vertices: readonly MapPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i, i += 1) {
    const vi = vertices[i]!;
    const vj = vertices[j]!;
    const straddles = vi.y > point.y !== vj.y > point.y;
    if (!straddles) continue;
    const xCross = ((vj.x - vi.x) * (point.y - vi.y)) / (vj.y - vi.y) + vi.x;
    if (point.x < xCross) inside = !inside;
  }
  return inside;
}

/** Whether `point` falls within `bbox`, inclusive of the edges. */
export function pointInBBox(point: MapPoint, bbox: BBox): boolean {
  return point.x >= bbox.minX && point.x <= bbox.maxX && point.y >= bbox.minY && point.y <= bbox.maxY;
}

/**
 * A hair past the edge, not on it. `pointInPolygon`'s ray-casting has the
 * usual half-open edge convention (two of a rectangle's four edges test as
 * "inside", two as "outside" — see its own doc comment): landing a pushed
 * point exactly on the bbox boundary would leave it ambiguously "inside"
 * for two of the four push directions. Clearing the edge by this much
 * guarantees the result is unambiguously outside the bbox — and so outside
 * the polygon — regardless of which edge it left by. Far below any unit
 * that matters on the map (a minute's walk is 1 map unit).
 */
const CLEARANCE = 1e-6;

/**
 * Push `point` outside `bbox` by the shortest distance to whichever edge it
 * is closest to, plus `CLEARANCE`. A polygon's bbox always contains the
 * polygon, so a point outside the bbox is guaranteed outside the polygon
 * too — the deliberate, documented approximation for a concave footprint
 * (none exist in current content; all are rectangles, where this is exact
 * up to `CLEARANCE`). A point already outside the bbox is returned
 * unchanged.
 */
export function pushOutOfBBox(point: MapPoint, bbox: BBox): MapPoint {
  if (!pointInBBox(point, bbox)) return point;
  const toLeft = point.x - bbox.minX;
  const toRight = bbox.maxX - point.x;
  const toBottom = point.y - bbox.minY;
  const toTop = bbox.maxY - point.y;
  const nearest = Math.min(toLeft, toRight, toBottom, toTop);
  if (nearest === toLeft) return { x: bbox.minX - CLEARANCE, y: point.y };
  if (nearest === toRight) return { x: bbox.maxX + CLEARANCE, y: point.y };
  if (nearest === toBottom) return { x: point.x, y: bbox.minY - CLEARANCE };
  return { x: point.x, y: bbox.maxY + CLEARANCE };
}
